import React, { useEffect, useMemo, useState } from 'react';

import { useIntl } from 'react-intl';

import LoadingAnimation from 'components/loading-animation';
import Table from 'components/table';
import { useGetLoyaltyCardTransactionsQuery } from 'generated/rbi-graphql';
import { useLoyaltyActiveCard } from 'hooks/loyalty';
import {
  MonthHeader,
  PurchaseAmountHeader,
  RewardsHeader,
  TableWrap,
} from 'pages/loyalty/loyalty-history/loyalty-history.styled';
import { Transaction, TransactionTable } from 'pages/loyalty/loyalty-history/types';
import { useLocalizedTransactionTransformer } from 'pages/loyalty/transactions/use-localized-transaction-transformer.th';
import {
  groupAndSortTransactionsByMonth,
  useTransactionDateFormatters,
} from 'pages/loyalty/transactions/utils';
import { useCustomizableContent } from 'state/customizable-content';

import { SectionDescription, SectionHeader } from './styled';

interface THTransaction extends Omit<Transaction, 'rewards'> {
  rewards?: React.ReactNode;
}

const TransactionHistory = () => {
  const [tableData, setTableData] = useState<THTransaction[]>([]);
  const { activeCard } = useLoyaltyActiveCard();
  const { loyaltyContent } = useCustomizableContent();
  const { formatMessage } = useIntl();
  const { getMonth } = useTransactionDateFormatters();

  const { loading, data: transactionsData } = useGetLoyaltyCardTransactionsQuery({
    variables: {
      cardId: activeCard ? activeCard.cardId : '',
    },
    skip: !activeCard || !activeCard.cardId,
  });
  const transactions = useMemo(
    () =>
      transactionsData && transactionsData.getLoyaltyCardTransactions
        ? transactionsData.getLoyaltyCardTransactions.transactions
        : [],
    [transactionsData]
  );

  const transactionTransformer = useLocalizedTransactionTransformer();

  useEffect(() => {
    if (transactions) {
      setTableData(transactions.map(transactionTransformer));
    }
  }, [transactions, transactionTransformer]);

  const tables: TransactionTable[] = useMemo(() => {
    const groupedAndSortedTransactions = groupAndSortTransactionsByMonth(tableData);

    return groupedAndSortedTransactions.map(([, data]) => ({
      head: [
        {
          dateTime: <MonthHeader>{getMonth(data[0])}</MonthHeader>,
          rewards: <RewardsHeader>{formatMessage({ id: 'rewards' })}</RewardsHeader>,
          purchaseAmount: (
            <PurchaseAmountHeader>{formatMessage({ id: 'purchaseAmount' })}</PurchaseAmountHeader>
          ),
        },
      ],
      body: data.map(({ dateTime, rewards, purchaseAmount }) => ({
        dateTime,
        rewards,
        purchaseAmount,
      })),
    }));
  }, [tableData, formatMessage, getMonth]);

  if (loading) {
    return <LoadingAnimation fillColor={Styles.color.primary} />;
  }

  return (
    <>
      <SectionHeader>{loyaltyContent?.historyPageText?.locale}</SectionHeader>
      <SectionDescription>{loyaltyContent?.historyPageSubtext?.locale}</SectionDescription>
      <SectionDescription>{loyaltyContent?.pointsUpdateText?.locale}</SectionDescription>
      {tables.map((table, idx) => (
        <TableWrap key={`historyTable.${idx}`} idx={idx}>
          <Table table={table} />
        </TableWrap>
      ))}
    </>
  );
};

export default TransactionHistory;
